<template>
  <div>
    <data-table 
      ref="dataTableRef"
      :config="tableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      :autoLoad="false"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="handleRowClick"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '../../grid/DataTable.vue';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';
import { permissionsService } from '../../services/permissionsService';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

interface GridButtonClickPayload {
  value?: Id;
  data?: Record<string, unknown>;
  action: string;
}

interface Props {
  usuarioVersao?: number;
}

const props = withDefaults(defineProps<Props>(), {
  usuarioVersao: 0
});

const emit = defineEmits<{
  'edit-compromisso': [compromissoId: Id];
  'cliente.edit': [clienteId: Id];
  'checkout-compromisso': [compromissoId: Id];
}>();

// Get i18n composable
const { t } = useI18n();
const { initialized } = useFiltro();

// Reactive data
const dataTableRef = ref<DataTableInstance | null>(null);

// Computed properties
const endpoint = computed<string>(() => '/index.php/compromissos/lista_compromissos');

const tableColumns = computed(() => {
  const columns: Record<string, unknown>[] = [
    {
      data: 'compromisso_id',
      title: t('ID'),
      width: 100,
      resizable: true,
      sortable: true,
      renderType: 'clickable_id',
      dataType: 'number'
    },
    {
      data: 'vendedor',
      title: t('Vendedor'),
      width: 150,
      resizable: true,
      sortable: true,
      dataType: 'text'
    },
    {
      data: 'operador',
      title: t('Operador'),
      width: 150,
      resizable: true,
      sortable: true,
      dataType: 'text'
    },
    {
      data: 'franquia',
      title: t('Franquia'),
      width: 150,
      resizable: true,
      sortable: true,
      dataType: 'text'
    },
    {
      data: 'nome_cliente',
      title: t('Cliente'),
      width: 150,
      resizable: true,
      sortable: true,
      dataType: 'text',
      // If user is NOT Operador, make it clickable
      renderType: getUserGroup() === 'Operador' ? undefined : 'custom_clickable_id',
      clickCallback: (_value: Id | undefined, data: { cliente_id?: Id; id?: Id } | undefined) => {
        // Only allow editing for non-Operador users
        if (getUserGroup() !== 'Operador') {
          const clienteId = data?.cliente_id || data?.id || 0;
          log('CompromissoTable', 'clickCallback', 'Cliente clicked', clienteId);
          editCliente(clienteId);
        }
      }
    },
    {
      data: 'bairro',
      title: t('Bairro'),
      width: 150,
      resizable: true,
      sortable: true,
      dataType: 'text'
    },
    {
      data: 'total',
      title: t('Total'),
      width: 120,
      resizable: true,
      sortable: true,
      renderType: 'currency',
      dataType: 'number',
      footerType: 'sum',
      footerCurrency: true
    },
    {
      data: 'compromisso_data_hora_inicio',
      title: t('Início'),
      resizable: true,
      sortable: true,
      dataType: 'date'
    },
    {
      data: 'compromisso_data_hora_fim',
      title: t('Fim'),
      resizable: true,
      sortable: true,
      dataType: 'date'
    },
    {
      data: 'status',
      title: t('Status'),
      resizable: true,
      sortable: true,
      dataType: 'text'
    },
    {
      data: 'forma_de_pagamento',
      title: t('Forma de Pagamento'),
      resizable: true,
      sortable: true,
      dataType: 'text'
    },
    {
      data: 'compromisso_id',
      title: t('Checkout'),
      width: 100,
      resizable: true,
      sortable: false,
      renderType: 'clickable_button',
      buttonClass: 'p-button-warning p-button-sm',
      buttonText: t('Checkout'),
      buttonCondition: (rowData: { status_raw?: string; status?: string } | undefined): boolean => {
        const statusRaw = rowData?.status_raw || rowData?.status || '';
        return statusRaw === 'Em andamento';
      },
      buttonAction: 'checkout'
    }
  ];

  // Add financial columns for version 1 users
  if (props.usuarioVersao === 1) {
    columns.push({
      data: 'status_financeiro',
      title: t('Status Financeiro'),
      resizable: true,
      sortable: true
    });
    columns.push({
      data: 'valor_recebido',
      title: t('Recebido'),
      width: 120,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams): string => {
        const value = parseFloat(String(params.value ?? '')) / 100 || 0;
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(value);
      }
    });
  }

  return columns;
});

const tableConfig = computed(() => {
  return {
    tableId: 'tabelaCompromissos',
    title: t('Relatório de Compromissos'),
    filename: `compromissos_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    // Chamado 2026-0041: mede a própria posição e ocupa até o fim da janela
    // (src/grid/tableHeight.ts). Nenhum ancestral precisa ter altura definida.
    heightMode: 'fill-viewport',
    pagination: true,
    rowSelection: 'single',
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.compromisso_id;
      return id == null ? '' : `compromisso-row-${id}`;
    }
  };
});

const filterConfig = computed(() => {
  return {
    searchName: t('Procurar'),
    choosePlaceholder: t('Escolha'),
    refreshName: t('Atualizar'),
    printName: t('Imprimir'),
    debug: false,
    columns: {
      'compromisso_id': 'number',
      'vendedor': 'select',
      'operador': 'select',
      'franquia': 'select',
      'nome_cliente': 'text',
      'bairro': 'text',
      'total': 'number',
      'inicio': 'date',
      'fim': 'date',
      'status': 'select',
      'forma_de_pagamento': 'select'
    }
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV'),
    loading: false
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

// Methods

const getUserGroup = (): string => {
  // Get user group from permissionsService singleton
  try {
    return permissionsService.getUserGroup() || '';
  } catch (error) {
    log('CompromissoTable', 'getUserGroup', 'Error getting user group', error);
    return '';
  }
};

const loadData = (): void => {
  log('CompromissoTable', 'loadData', 'Triggering grid refresh');
  if (dataTableRef.value) {
    dataTableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('CompromissoTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t(`Carregados`) + ` ${data.length} ` + t(`registros de compromissos`));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('CompromissoTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : (error?.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar compromissos: ') + message);
};

const editItem = (compromissoId: Id): void => {
  log('CompromissoTable', 'editItem', 'Editing ID', compromissoId);
  // Emit event to parent component to handle editing
  emit('edit-compromisso', compromissoId);
};

const editCliente = (clienteId: Id): void => {
  log('CompromissoTable', 'editCliente', 'Editing client ID', clienteId);

  // Emit to eventBus for global modal
  eventBus.emit('cliente.edit', clienteId);

  // Also emit to parent component (for backward compatibility)
  emit('cliente.edit', clienteId);
};

const checkoutItem = (compromissoId: Id): void => {
  log('CompromissoTable', 'checkoutItem', 'Checkout ID', compromissoId);
  // Emit event to parent component to handle checkout
  emit('checkout-compromisso', compromissoId);
};

const handleRowClick = (rowData: { compromisso_id?: Id } | undefined): void => {
  log('CompromissoTable', 'handleRowClick', 'Row clicked', rowData?.compromisso_id || null);
  // Handle row click if needed
};

const handleEditClick = (compromissoId: Id): void => {
  log('CompromissoTable', 'handleEditClick', 'Edit clicked for ID', compromissoId);
  editItem(compromissoId);
};

const handleButtonClick = ({ value, data, action }: GridButtonClickPayload): void => {
  log('CompromissoTable', 'handleButtonClick', 'Button clicked', { value, data, action });
  if (action === 'checkout' && value !== undefined) {
    checkoutItem(value);
  }
};

const handleFilterUpdated = (): void => {
  log('CompromissoTable', 'event:filter.updated', 'Received, reloading data');
  loadData();
};

const handleCompromissoSaved = (): void => {
  log('CompromissoTable', 'event:compromisso.saved', 'Received, reloading data');
  loadData();
};

const handleCompromissoDeleted = (): void => {
  log('CompromissoTable', 'event:compromisso.deleted', 'Received, reloading data');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('CompromissoTable', 'mounted', 'Component mounted');
  log('CompromissoTable', 'mounted', 'Filter initialized status:', initialized.value);

  // Always trigger initial load - DataTable will handle it via autoLoad
  // but we also manually trigger it here to ensure it loads even if filter isn't ready
  log('CompromissoTable', 'mounted', 'Triggering initial data load');
  loadData();

  // Listen for filter updates
  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('compromisso.saved', handleCompromissoSaved);
  eventBus.on('compromisso.deleted', handleCompromissoDeleted);
});

onBeforeUnmount(() => {
  log('CompromissoTable', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up event listeners
  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('compromisso.saved', handleCompromissoSaved);
  eventBus.off('compromisso.deleted', handleCompromissoDeleted);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
.text-center {
  text-align: center;
}

:deep(.clickable-link) {
  color: #3b82f6;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
}

:deep(.clickable-link:hover) {
  text-decoration: underline;
}

:deep(.clickable-link i) {
  font-size: 0.875rem;
  margin-left: 0.25rem;
}
</style>
